import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SCAN_PROFILE_HINT, SCAN_PROFILE_LABEL, SCAN_PROFILES, SECURITY_ENGINE_HINT, SECURITY_ENGINE_LABEL, SECURITY_ENGINES, missingInputGroups, type ScanProfile, type SecurityEngine } from '@1qa/shared';
import clsx from 'clsx';
import { ArrowLeft, Play, ShieldCheck, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { SecurityScopeCard } from '@/components/security/SecurityScopeCard';
import { CodeRepoCard, CodeRepoPanel } from '@/components/security/CodeRepoPanel';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunCollection } from '@/lib/runCollection';
import { Button, Card, CardBody, CardHeader, Dialog, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, StatusPill, useConfirm, useToast } from '@/components/ui';

function hostOf(url?: string | null) {
  try {
    return url ? new URL(url).hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Create a security test (a scan configuration): just a name, like naming any test. */
function NewSecurityTest({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('Security scan');
  const { can } = useAuth();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  // Project Admins can connect the app's code here already, for the code-level security audit.
  const canManageRepo = !!project.data && can('security.scope.manage', project.data.orgId);
  const create = useMutation({
    mutationFn: () => api.createSecurityTest(projectId, { name: name.trim() }),
    onSuccess: (t) => navigate(`/projects/${projectId}/security/${t.id}`, { replace: true }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-lg">
      <Link to={`/projects/${projectId}`} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ArrowLeft className="size-4" /> Back
      </Link>
      <Card>
        <CardHeader
          title="New security scan"
          description="A scan of this project's app. You confirm which host it may test, then run a black-box pentest or a code-level security audit (which reads the app's GitHub / GitLab code)."
        />
        <CardBody className="space-y-4">
          <Field label="Name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()} icon={<ShieldCheck className="size-4" />}>
              Create
            </Button>
          </div>
        </CardBody>
      </Card>
      {canManageRepo && (
        <div className="mt-5">
          <CodeRepoCard projectId={projectId} canManage />
        </div>
      )}
    </div>
  );
}

function RunDialog({ projectId, orgId, testId, open, onClose }: { projectId: string; orgId: string; testId: string; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<ScanProfile>('QUICK');
  const [engine, setEngine] = useState<SecurityEngine>('CLAUDE_RED');
  const { can } = useAuth();
  // The code-level audit reads the app's code: it needs a connected repository (connected right here if missing).
  const repo = useQuery({ queryKey: ['code-repo', projectId], queryFn: () => api.codeRepo(projectId), enabled: open });
  const needsRepo = engine === 'SHANNON' && !repo.data;
  // The test data collection (environment) the scan uses — its credentials reach the pentester. Shared per
  // project with every other Run button; "No collection" uses the shared/default values.
  const collection = useRunCollection(projectId);

  // Standard-audit inputs: a test login, a second account, a test email. Read the project + org test data; any
  // group not fully present is offered for entry here and saved as project test data before the run starts.
  const projVars = useQuery({ queryKey: ['variables', projectId], queryFn: () => api.projectVariables(projectId), enabled: open });
  const orgVars = useQuery({ queryKey: ['org-variables', orgId], queryFn: () => api.orgVariables(orgId), enabled: open });
  const providedKeys = useMemo(() => [...(projVars.data ?? []), ...(orgVars.data ?? [])].map((v) => v.key), [projVars.data, orgVars.data]);
  const missing = useMemo(() => missingInputGroups(providedKeys), [providedKeys]);
  const [inputVals, setInputVals] = useState<Record<string, string>>({});

  const run = useMutation({
    mutationFn: async () => {
      // Save any inputs the user filled in as project test data (so the pentester can use them), then run.
      for (const g of missing)
        for (const k of g.keys) {
          const val = (inputVals[k.name] ?? '').trim();
          if (val && !providedKeys.includes(k.name)) await api.createProjectVariable(projectId, { key: k.name, value: val, isSecret: k.secret });
        }
      return api.triggerSecurityRun(projectId, testId, profile, engine, collection.collectionId);
    },
    onSuccess: (r) => navigate(`/runs/${r.id}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onClose={onClose} title="Run a security scan" description="Pick the engine and how hard the scan tries. You can stop it from the report.">
      <div className="space-y-3">
        <div className="text-xs font-semibold tracking-label text-fg-secondary uppercase">Engine</div>
        {SECURITY_ENGINES.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setEngine(e)}
            className={clsx(
              'flex w-full flex-col rounded-card p-4 text-left ring-1 ring-inset transition',
              engine === e ? 'bg-primary-subtle/40 ring-primary' : 'bg-surface ring-border hover:ring-primary/60',
            )}
          >
            <span className="text-sm font-semibold text-fg">{SECURITY_ENGINE_LABEL[e]}</span>
            <span className="mt-0.5 text-xs text-fg-muted">{SECURITY_ENGINE_HINT[e]}</span>
          </button>
        ))}
        {engine === 'SHANNON' && (
          <div className="rounded-card bg-surface p-4 ring-1 ring-border ring-inset">
            <div className="mb-3 text-xs font-semibold tracking-label text-fg-secondary uppercase">Code repository</div>
            <CodeRepoPanel projectId={projectId} canManage={can('security.scope.manage', orgId)} />
          </div>
        )}
        <div className="pt-1 text-xs font-semibold tracking-label text-fg-secondary uppercase">Depth</div>
        {SCAN_PROFILES.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setProfile(p)}
            className={clsx(
              'flex w-full flex-col rounded-card p-4 text-left ring-1 ring-inset transition',
              profile === p ? 'bg-primary-subtle/40 ring-primary' : 'bg-surface ring-border hover:ring-primary/60',
            )}
          >
            <span className="text-sm font-semibold text-fg">{SCAN_PROFILE_LABEL[p]}</span>
            <span className="mt-0.5 text-xs text-fg-muted">{SCAN_PROFILE_HINT[p]}</span>
          </button>
        ))}
        {collection.collections.length > 0 && (
          <div className="pt-1">
            <div className="mb-2 text-xs font-semibold tracking-label text-fg-secondary uppercase">Test data</div>
            <RunCollectionPicker state={collection} />
            <p className="mt-1.5 text-xs text-fg-muted">The pentester uses this environment's test data (e.g. a test login) to probe authenticated areas.</p>
          </div>
        )}
        {missing.length > 0 && (
          <div className="rounded-card bg-surface p-3 ring-1 ring-border ring-inset">
            <div className="text-xs font-semibold tracking-label text-fg-secondary uppercase">Inputs this audit needs</div>
            <p className="mt-1 text-xs text-fg-muted">
              Some standard tests need these. Fill in what you can — anything left blank means those checks are reported “not tested”. Saved as this project's test data.
            </p>
            <div className="mt-3 space-y-3">
              {missing.map((g) => (
                <div key={g.id}>
                  <div className="text-sm font-medium text-fg">{g.label}</div>
                  <div className="text-xs text-fg-muted">For {g.why}.</div>
                  <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                    {g.keys.map((k) => (
                      <Input
                        key={k.name}
                        type={k.secret ? 'password' : 'text'}
                        placeholder={k.label}
                        value={inputVals[k.name] ?? ''}
                        onChange={(e) => setInputVals((v) => ({ ...v, [k.name]: e.target.value }))}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => run.mutate()}
            loading={run.isPending}
            disabled={needsRepo}
            title={needsRepo ? 'Connect the code repository first' : undefined}
            icon={<Play className="size-4" />}
          >
            Start scan
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A security test: the project's security scope (authorized hosts + authorization), a Run button that opens the
 * Quick/Deep dialog, and this project's past scans. /security/new creates one. Its creator or a Project Admin runs it.
 */
export function SecurityTestPage() {
  const { projectId = '', testId } = useParams();
  const isNew = !testId;
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can, canModify } = useAuth();

  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const test = useQuery({ queryKey: ['test', testId], queryFn: () => api.test(testId!), enabled: !isNew });
  const runs = useQuery({ queryKey: ['runs', projectId], queryFn: () => api.projectRuns(projectId), enabled: !isNew, refetchInterval: 10_000 });
  const [runOpen, setRunOpen] = useState(params.get('run') === '1');
  const del = useMutation({
    mutationFn: () => api.deleteTest(testId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tests', projectId] });
      toast.success('Security test deleted');
      navigate(`/projects/${projectId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isNew) return <NewSecurityTest projectId={projectId} />;
  if (project.isLoading || test.isLoading) return <PageLoader />;
  if (project.error || !project.data) return <ErrorNotice error={project.error} />;
  if (test.data && test.data.type !== 'SECURITY') return <Navigate to={`/projects/${projectId}/tests/${testId}`} replace />;

  const orgId = project.data.orgId;
  const canRun = can('security.run', orgId) && canModify('test.write', test.data?.createdById, orgId);
  const canManageScope = can('security.scope.manage', orgId);
  const openRun = () => {
    setRunOpen(true);
    const n = new URLSearchParams(params);
    n.delete('run');
    setParams(n, { replace: true });
  };
  // Only this security test's own scans (a security run covers exactly one test).
  const scans = (runs.data ?? []).filter((r) => r.securityPlan && r.summary?.testCaseIds?.includes(testId!));

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ArrowLeft className="size-4" /> {project.data.name}
      </Link>
      <PageHeader
        title={test.data?.name ?? 'Security scan'}
        actions={
          <div className="flex gap-2">
            {canRun && <Button onClick={openRun} icon={<Play className="size-4" />}>Run scan</Button>}
            {canModify('test.manage.any', test.data?.createdById, orgId) && (
              <Button
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                onClick={() =>
                  confirm({ title: 'Delete this security test?', confirmLabel: 'Delete', tone: 'danger' }).then((ok) => ok && del.mutate())
                }
              >
                Delete
              </Button>
            )}
          </div>
        }
      />

      <SecurityScopeCard projectId={projectId} canManage={canManageScope} suggestHost={hostOf(project.data.targetUrl)} />

      <CodeRepoCard projectId={projectId} canManage={canManageScope} />

      <Card>
        <CardHeader title="Scans" description="This project's security scans. Open one for its findings and fixes." />
        <CardBody>
          {runs.isLoading ? (
            <PageLoader />
          ) : scans.length === 0 ? (
            <EmptyState title="No scans yet" description="Set the scope above, then press “Run scan”." />
          ) : (
            <ul className="divide-y divide-border">
              {scans.map((r) => (
                <li key={r.id}>
                  <Link to={`/runs/${r.id}`} className="flex items-center gap-3 px-1 py-3 hover:bg-surface-raised/50">
                    <StatusPill status={r.status} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg">
                        {r.securityPlan?.engine ? `${SECURITY_ENGINE_LABEL[r.securityPlan.engine]} · ` : ''}
                        {r.securityPlan?.profile === 'DEEP' ? 'Deep scan' : 'Quick scan'}
                      </span>
                      <span className="block text-xs text-fg-muted">
                        {timeAgo(r.queuedAt)}
                        {r.collectionName && ` · ${r.collectionName} test data`}
                        {r.triggeredBy && ` · by ${r.triggeredBy.name}`}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {testId && <RunDialog projectId={projectId} orgId={orgId} testId={testId} open={runOpen} onClose={() => setRunOpen(false)} />}
    </div>
  );
}
